import type { Transaction } from '@finapp/types';
import type React from 'react';

interface TransactionTableViewProps {
  transactions: Transaction[];
  selectedItems: Set<number>;
  userRole: string;
  onEdit: (transaction: Transaction) => void;
  onDelete: (transactionId: number) => void;
  onSelect: (transactionId: number) => void;
  onSelectAll: () => void;
}

const TransactionTableView: React.FC<TransactionTableViewProps> = ({
  transactions,
  selectedItems,
  userRole,
  onEdit,
  onDelete,
  onSelect,
  onSelectAll,
}) => {
  return (
    <div className="hidden lg:block overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th className="relative px-6 py-3">
              <input
                type="checkbox"
                className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={selectedItems.size === transactions.length && transactions.length > 0}
                onChange={onSelectAll}
              />
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Heading / Description
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Costpoints
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Tags
            </th>
            <th className="relative px-6 py-3">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {transactions.map((transaction) => (
            <tr
              key={transaction.id}
              className={`hover:bg-gray-50 ${
                selectedItems.has(transaction.id) ? 'bg-blue-50' : ''
              }`}
            >
              <td className="relative px-6 py-4 whitespace-nowrap">
                <input
                  type="checkbox"
                  className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={selectedItems.has(transaction.id)}
                  onChange={() => onSelect(transaction.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {new Date(transaction.date).toLocaleDateString()}
              </td>
              <td className="px-6 py-4 text-sm text-gray-900">
                <div className="max-w-xs">
                  <div
                    className="font-medium truncate"
                    title={transaction.heading || transaction.original_heading}
                  >
                    {transaction.heading || transaction.original_heading || 'No heading'}
                  </div>
                  {transaction.description && (
                    <div
                      className="text-gray-500 text-xs truncate mt-1"
                      title={transaction.description}
                    >
                      {transaction.description}
                    </div>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex flex-col">
                  <span
                    className={transaction.type === 'deposit' ? 'text-green-600' : 'text-red-600'}
                  >
                    {transaction.type === 'deposit' ? '+' : '-'}
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'EUR',
                      minimumFractionDigits: 2,
                    }).format(Math.abs(transaction.amount))}
                    {transaction.has_vat && (
                      <span className="text-xs text-gray-500 ml-1">(gross)</span>
                    )}
                  </span>
                  {transaction.has_vat && (
                    <div className="text-xs text-gray-600 mt-1">
                      <div>
                        VAT (
                        {typeof transaction.vat_rate === 'object'
                          ? transaction.vat_rate.rate
                          : transaction.vat_rate}
                        %):{' '}
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'EUR',
                          minimumFractionDigits: 2,
                        }).format(transaction.vat_amount || 0)}
                      </div>
                      <div className="font-medium">
                        Net:{' '}
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'EUR',
                          minimumFractionDigits: 2,
                        }).format(transaction.net_amount || transaction.amount)}
                      </div>
                    </div>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    transaction.type === 'deposit'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {transaction.type === 'deposit' ? 'Income' : 'Expense'}
                </span>
              </td>
              <td className="px-6 py-4 text-sm text-gray-500">
                <div className="flex flex-wrap gap-1">
                  {transaction.costpoints.length > 0 ? (
                    transaction.costpoints.map((cp, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                      >
                        {cp.costpoint.toUpperCase()}: €{cp.amount.toFixed(2)}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400 italic">No costpoints</span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-gray-500">
                <div className="flex flex-wrap gap-1">
                  {(() => {
                    // Collect all tags from transaction and costpoints
                    const allTags = [...(transaction.tags || [])];

                    // Add costpoint-level tags
                    transaction.costpoints?.forEach((costpoint) => {
                      const costpointTags = Array.isArray(costpoint.tags) ? costpoint.tags : [];
                      costpointTags.forEach((tag) => {
                        if (tag?.id && !allTags.find((existingTag) => existingTag.id === tag.id)) {
                          allTags.push(tag);
                        }
                      });
                    });

                    return allTags.length > 0 ? (
                      allTags.map((tag, index) => (
                        <span
                          key={`${tag.id}-${index}`}
                          className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full"
                          title={tag.display_name}
                        >
                          {tag.display_name}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400 italic">No tags</span>
                    );
                  })()}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end space-x-2">
                  <button
                    onClick={() => onEdit(transaction)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </button>
                  {(userRole === 'admin' || userRole === 'superadmin') && (
                    <button
                      onClick={() => onDelete(transaction.id)}
                      className="text-red-600 hover:text-red-900 p-1"
                      title="Delete transaction"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TransactionTableView;
